import {
  authenticate,
  getSelf,
  register,
  googleConnect,
  generateBusinessMicrosoftUrl,
  generatePersonalMicrosoftUrl,
  generatePersonalMicrosoftReauthUrl,
  generatePersonalGoogleReauthUrl,
  generateDropboxUrl,
  generateBoxUrl,
  generateSalesforceUrl,
  generateEntraIdUrl,
  generatePersonalBoxReauthUrl,
  generatePersonalDropboxReauthUrl,
  entraIdConnect,
  groupConnect,
} from "@/services/api/app/auth";
import Store from "@/store/app/index";
import i18n from "@/boot/i18n";

let ENVIRONMENT = 'staging';
if (process.env.VUE_APP_ENVIRONMENT) {
  ENVIRONMENT = process.env.VUE_APP_ENVIRONMENT;
}

const RESELLER_BRANDING_KEY = 'idrive-reseller-branding'

function readCachedResellerBranding() {
  try {
    return JSON.parse(localStorage.getItem(RESELLER_BRANDING_KEY) || 'null')
  } catch (e) {
    return null
  }
}

function cacheResellerBranding(resellerInfo) {
  localStorage.setItem(RESELLER_BRANDING_KEY, JSON.stringify({
    brandName: resellerInfo?.brandName || null,
    favIconURL: resellerInfo?.favIconURL || null,
  }))
}

function setResellerFavicon(resellerInfo) {
  if (!resellerInfo?.favIconURL) {
    return
  }
  const link = document.querySelector("link[rel~='icon']") || document.createElement('link')
  link.rel = 'icon'
  link.href = resellerInfo.favIconURL
  document.head.appendChild(link)
}

function enforceResellerTheme(resellerInfo) {
  if (!resellerInfo) {
    return
  }
  if (localStorage.getItem('idrive-theme') === 'dark') {
    localStorage.setItem('idrive-theme', 'default')
    window.location.reload()
  }
}

function redirectToResellerDomain(user) {
  const domainName = user?.resellerInfo?.domainName
  if (ENVIRONMENT !== 'production' || !domainName || !user?.id) {
    return false
  }
  const targetHost = `${user.id}.${domainName}`.toLowerCase()
  if (window.location.hostname.toLowerCase() === targetHost) {
    return false
  }
  const {pathname, search, hash} = window.location
  window.location.replace(`https://${targetHost}${pathname}${search}${hash}`)
  return true
}

function setOsanoLocale(lang) {
  try {
    if (window.Osano?.cm) {
      window.Osano.cm.locale = lang;
    } else if (typeof window.Osano === 'function') {
      window.Osano('locale', lang);
    }
  } catch (error) {
    console.error('Error setting Osano locale:', error);
  }
}

const defaultState = () => ({
  self: {},
  isLoading: true,
  accessToken: null,
  googleTenant: {}
})

export default {
  namespaced: true,

  actions: {
    getSelf({commit}) {
      return getSelf()
        .then(user => {
          commit('setSelf', user);
          if (redirectToResellerDomain(user)) {
            return user;
          }
          const lang = user.lang || 'en'
          i18n.global.locale.value = lang
          localStorage.setItem('lang', lang)
          setOsanoLocale(lang)
          cacheResellerBranding(user.resellerInfo)
          setResellerFavicon(user.resellerInfo)
          enforceResellerTheme(user.resellerInfo)
          return user;
        })
        .catch(() => {
          commit('resetState')
          return Promise.reject();
        })
    },
    register({commit}, registerForm) {
      return register(registerForm)
        .then((result) => {
          commit('setAccessToken', result.accessToken)
        })
    },
    authenticate({commit}, authenticateForm) {
      return authenticate(authenticateForm)
        .then(result => {
          commit('setAccessToken', result.accessToken)
          return result
        })
    },
    generateBusinessMicrosoftUrl({commit, dispatch}) {
      return generateBusinessMicrosoftUrl();
    },
    entraIdConnect({commit, dispatch}) {
      return entraIdConnect();
    },
    generatePersonalMicrosoftUrl({commit, dispatch}) {
      return generatePersonalMicrosoftUrl();
    },
    generatePersonalMicrosoftReauthUrl({commit, dispatch}) {
      return generatePersonalMicrosoftReauthUrl();
    },
    generatePersonalBoxReauthUrl({commit, dispatch}) {
      return generatePersonalBoxReauthUrl();
    },
    generatePersonalDropboxReauthUrl({commit, dispatch}) {
      return generatePersonalDropboxReauthUrl();
    },
    generatePersonalGoogleReauthUrl({commit, dispatch}) {
      return generatePersonalGoogleReauthUrl();
    },
    googleConnect({commit, dispatch}, data) {
      return googleConnect(data)
        .then(() => {
          return dispatch('getSelf')
        })
    },
    groupConnect({commit, dispatch}) {
      return groupConnect();
    },
    generateDropboxUrl({commit, dispatch}, levelType) {
      return generateDropboxUrl(levelType);
    },
    generateBoxUrl({commit, dispatch}, levelType) {
      return generateBoxUrl(levelType);
    },
    generateSalesforceUrl({commit, dispatch}, levelType) {
      return generateSalesforceUrl(levelType);
    },
    generateEntraIdUrl({commit, dispatch}, levelType) {
      return generateEntraIdUrl(levelType);
    },
    signOut({commit, state}, redirectEnabled = true) {
      if (ENVIRONMENT === 'production' && redirectEnabled) {
        if (state.self?.platform === "360") {
          window.location.href = "https://webapp.idrive360.com/api/v1/logout"
        } else if (state.self?.platform === "rpc") {
          window.location.href = "https://login.remotepc.com/rpcnew/logout"
        } else if (state.self?.platform === "rpc") {
          window.location.href = "https://login.remotepc.com/rpcnew/logout"
        } else if (state.self?.platform === "ibackup") {
          window.location.href = "https://www.ibackup.com/newibackup/logout"
        } else if (state.self?.platform === "c2c-reseller") {
          window.location.href = "https://reseller.idrive.com/app/logout"
        } else {
          window.location.href = 'https://www.idrive.com/idrive/logout'
        }
      } else {
        commit('resetState')
      }
    },
  },

  mutations: {
    setSelf(state, user) {
      state.self = user
    },
    setAccessToken(state, accessToken) {
      state.accessToken = accessToken
    },
    resetState(state) {
      Object.assign(state, defaultState())
    },
    getGoogleTenant(state, googleTenant) {
      state.googleTenant = googleTenant
    },
  },

  getters: {
    isReseller: (state) => {
      return !!state.self?.resellerInfo;
    },
    resellerLogo: (state) => {
      return state.self?.resellerInfo?.logoURL || null;
    },
    resellerLogoBackground: (state) => {
      return state.self?.resellerInfo?.backgroundLogoColor || null;
    },
    resellerFavIcon: (state) => {
      return state.self?.resellerInfo?.favIconURL || null;
    },
    resellerCopyright: (state) => {
      const name = state.self?.resellerInfo?.copyRightName?.trim();
      if (!name) return null;
      return name.startsWith('©') ? name : `©${name}`;
    },
    resellerBrandName: (state) => {
      if (state.self?.resellerInfo?.brandName) {
        return state.self.resellerInfo.brandName;
      }
      if (!state.self?.id) {
        return readCachedResellerBranding()?.brandName || null;
      }
      return null;
    },
    resellerDomain: (state) => {
      const domain = state.self?.resellerInfo?.domainName;
      if (!domain) {
        return null;
      }
      const tenantId = state.self?.id;
      return tenantId ? `${tenantId}.${domain}` : domain;
    },
    isMicrosoftProviderEnabled: (state) => {
      return !!state.self?.providerTenants?.find(providerTenant => providerTenant.providerTenantType === 1);
    },
    isGoogleProviderEnabled: (state) => {
      return !!state.self?.providerTenants?.find(providerTenant => providerTenant.providerTenantType === 2);
    },
    isDropboxProviderEnabled: (state) => {
      return !!state.self?.providerTenants?.find(providerTenant => providerTenant.providerTenantType === 3);
    },
    isBoxProviderEnabled: (state) => {
      return !!state.self?.providerTenants?.find(providerTenant => providerTenant.providerTenantType === 4);
    },
    isSalesforceProviderEnabled: (state) => {
      return !!state.self?.providerTenants?.find(providerTenant => providerTenant.providerTenantType === 5);
    },
    isEntraIdProviderEnabled: (state) => {
      return !!state.self?.providerTenants?.find(providerTenant => providerTenant.providerTenantType === 6);
    },
    isIDrivePlatform: (state) => {
      return !['360', 'rpc', 'ibackup', 'c2c-reseller', 'salesforce'].includes(state.self?.platform);
    },
    isProviderConnected: (state) => (providerType) => {
      return !!state.self?.providerTenants?.find(providerTenant => providerTenant.providerTenantType === providerType);
    },
    isSuperAdmin: (state) => {
      return state.self.roles?.find(role => role.name === 'SuperAdmin')
    },
    accessLevel: (state) => {
      return state.self?.accessLevel;
    },
    /**
     * Usage: `hasAccessRights(['complete-access', 'system-access'])`
     */
    hasAccessRights: (state) => (accessRights) => {
      const items = Array.isArray(accessRights) ? accessRights : [accessRights];
      return items.includes(state.self?.accessLevel);
    },
    currentProviderTenant: (state, getters, rootState) => {
      const providerTenant = rootState.providerTenant;
      if (!providerTenant || !Array.isArray(state.self?.providerTenants)) {
        return undefined;
      }
      return state.self.providerTenants.find(
        t => t.providerTenantName?.toLowerCase() === providerTenant.toLowerCase()
      );
    },
    isPersonalTenant: (state, getters) => {
      return getters.currentProviderTenant?.levelType === 2;
    },
    /**
     * Usage: `hasProviderAccessRights([1, 3])` or `hasProviderAccessRights('entra-enabled')`.
     * Accepts level type numbers and the `entra-enabled` / `!entra-enabled` flags.
     */
    hasProviderAccessRights: (state, getters, rootState, rootGetters) => (levelTypes) => {
      const items = Array.isArray(levelTypes) ? levelTypes : [levelTypes];
      const providerLevelType = getters.providerLevelType;
      const isEntraEnabled = rootGetters['office/backupSettings/isEntraEnabled'];

      if (!providerLevelType && !isEntraEnabled) {
        return true;
      }

      for (const item of items) {
        if (typeof item === 'number') {
          if (providerLevelType && item === providerLevelType) {
            return true;
          }
          continue;
        }

        if (typeof item === 'string') {
          const negated = item.startsWith('!');
          const key = negated ? item.slice(1) : item;

          if (key === 'entra-enabled' && !getters.isPersonalTenant) {
            const val = !!isEntraEnabled;
            if ((val && !negated) || (!val && negated)) return true;
          }
        }
      }

      return false;
    },
    providerLevelType: (state) => {
      if (Store.state.providerTenant === 'GOOGLE') {
        return state.self?.providerTenants?.find(providerTenant => providerTenant?.providerTenantType === 2)?.levelType;
      } else if (Store.state.providerTenant === 'MICROSOFT') {
        return state.self?.providerTenants?.find(providerTenant => providerTenant?.providerTenantType === 1)?.levelType;
      } else if (Store.state.providerTenant === 'DROPBOX') {
        return state.self?.providerTenants?.find(providerTenant => providerTenant?.providerTenantType === 3)?.levelType;
      } else if (Store.state.providerTenant === 'BOX') {
        return state.self?.providerTenants?.find(providerTenant => providerTenant?.providerTenantType === 4)?.levelType;
      } else if (Store.state.providerTenant === 'SALESFORCE') {
        return state.self?.providerTenants?.find(providerTenant => providerTenant?.providerTenantType === 5)?.levelType;
      } else if (Store.state.providerTenant === 'ENTRAID') {
        return state.self?.providerTenants?.find(providerTenant => providerTenant?.providerTenantType === 6)?.levelType;
      }
    }

  },

  state: defaultState
}
